import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '../ui/Button';
import Loader from '../ui/Loader';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Pagination from '../ui/pagination';
import { ExternalCandidate, ExternalCandidateSearchParams } from '../../types/api';
import externalCandidatesService from '../../services/externalCandidates.service';
import externalCities from "../../config/external_cities.json";

interface ExternalCandidatesTableProps {
  isAdminView?: boolean;
  pageTitle: string;
  pageDescription: string;
}

type ColumnKey = 
  | 'Name' 
  | 'Age' 
  | 'Gender' 
  | 'YearsExperience' 
  | 'CurrentSalary' 
  | 'Skills' 
  | 'Industry' 
  | 'Location' 
  | 'Sublocation' 
  | 'QualificationLevel' 
  | 'Qualification' 
  | 'PreviousJob' 
  | 'Languages' 
  | 'ResumeActions';

interface VisibleColumns {
  [key: string]: boolean;
}

const COLUMN_OPTIONS: { key: ColumnKey; label: string }[] = [
  { key: 'Name', label: 'Name' },
  { key: 'Age', label: 'Age' },
  { key: 'Gender', label: 'Gender' },
  { key: 'YearsExperience', label: 'Years Experience' },
  { key: 'CurrentSalary', label: 'Current Salary' },
  { key: 'Skills', label: 'Skills' },
  { key: 'Industry', label: 'Industry' },
  { key: 'Location', label: 'Location' },
  { key: 'Sublocation', label: 'Sublocation' },
  { key: 'QualificationLevel', label: 'Qualification Level' },
  { key: 'Qualification', label: 'Qualification' },
  { key: 'PreviousJob', label: 'Previous Job' },
  { key: 'Languages', label: 'Languages' },
  { key: 'ResumeActions', label: 'Resume Actions' },
];

const DEFAULT_VISIBLE_COLUMNS: VisibleColumns = {
  Name: true,
  Age: true,
  Gender: true,
  YearsExperience: true,
  CurrentSalary: true,
  Skills: true,
  Industry: true,
  Location: true,
  Sublocation: true,
  QualificationLevel: true,
  Qualification: true,
  PreviousJob: false,
  Languages: false,
  ResumeActions: true,
};

const ExternalCandidatesTable: React.FC<ExternalCandidatesTableProps> = ({
  isAdminView = false,
  pageTitle,
  pageDescription,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Build city options from external cities data
  const cityOptions = useMemo(() => {
    const options = externalCities.map(city => ({
      value: city.id.toString(),
      label: `${city.cityName}, ${city.stateOrUt}`
    }));
    // Sort alphabetically by label
    options.sort((a, b) => a.label.localeCompare(b.label));
    // Add "All Cities" option at the top
    return [{ value: "", label: "All Cities" }, ...options];
  }, []);
  
  // Data state
  const [candidates, setCandidates] = useState<Map<number, ExternalCandidate[]>>(new Map().set(1, []));
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state - simplified initial values, read from URL in effect
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Filters state - default city is Vadodara (id: 147), read from URL in effect
  const [cityId, setCityId] = useState<number>(147);
  const [sublocation, setSublocation] = useState<string>("");
  const [skills, setSkills] = useState<string>("");
  const [industry, setIndustry] = useState<string>("");
  const [minExperience, setMinExperience] = useState<string>("");
  const [maxExperience, setMaxExperience] = useState<string>("");
  const [nameFilter, setNameFilter] = useState<string>("");
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<VisibleColumns>(() => {
    const savedColumns = searchParams.get("columns");
    if (savedColumns) {
      try {
        return { ...DEFAULT_VISIBLE_COLUMNS, ...JSON.parse(decodeURIComponent(savedColumns)) };
      } catch {
        return DEFAULT_VISIBLE_COLUMNS;
      }
    }
    return DEFAULT_VISIBLE_COLUMNS;
  });
  
  // Column dropdown state
  const [showColumnDropdown, setShowColumnDropdown] = useState<boolean>(false);
  const columnDropdownRef = useRef<HTMLDivElement>(null);

  // API loading flag to prevent concurrent calls
  const [apiLoading, setApiLoading] = useState<boolean>(false);

  // Migration loading state
  const [migrationLoading, setMigrationLoading] = useState<Map<number, boolean>>(new Map());

  // Internal state cache for display
  const [filteredCandidates, setFilteredCandidates] = useState<ExternalCandidate[]>([]);

  // Close column dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target as Node)) {
        setShowColumnDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update URL params - this will trigger the useEffect
  const updateSearchParams = useCallback((updates: Record<string, string | null>) => {
    const newSearchParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === "" || value === undefined) {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, value);
      }
    });

    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  // Fetch candidates from API
  const fetchCandidates = async (params: ExternalCandidateSearchParams) => {
    if (apiLoading) return; // Prevent concurrent calls
    setApiLoading(true);
    setLoading(true);
    setError(null);

    try {
      const response = await externalCandidatesService.searchExternalCandidates(params);
      console.log("[test] candidate fetch response", response)
      if (response.status === "success" && response.data) {
        const { items, totalCount, pageNumber, pageSize } = response.data;

        setCandidates(prev => new Map(prev).set(pageNumber, items));
        setTotalCount(totalCount);
        setTotalPages(Math.ceil(totalCount / pageSize));
        setFilteredCandidates(items);
      } else {
        setError(response.status || "Failed to fetch candidates");
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred while fetching candidates");
    } finally {
      setApiLoading(false);
      setLoading(false);
    }
  };

  // SINGLE effect - reacts to searchParams changes
  useEffect(() => {
    console.log("[ExternalCandidates] searchParams changed, triggering fetch");

    // Read all values from searchParams
    const page = parseInt(searchParams.get("page") || "1");
    const cityIdParam = parseInt(searchParams.get("cityId") || "147");
    const sublocParam = searchParams.get("sublocation") || "";
    const skillsParam = searchParams.get("skills") || "";
    const industryParam = searchParams.get("industry") || "";
    const minExpParam = searchParams.get("minExperience") || "";
    const maxExpParam = searchParams.get("maxExperience") || "";
    const nameParam = searchParams.get("name") || "";
    const pageSizeParam = parseInt(searchParams.get("itemsPerPage") || "10");

    // Update local state from URL
    setCurrentPage(page);
    setCityId(cityIdParam);
    setSublocation(sublocParam);
    setSkills(skillsParam);
    setIndustry(industryParam);
    setMinExperience(minExpParam);
    setMaxExperience(maxExpParam);
    setNameFilter(nameParam);
    setItemsPerPage(pageSizeParam);

    // Fetch data
    const params: ExternalCandidateSearchParams = {
      pageNumber: page,
      pageSize: pageSizeParam,
      cityId: cityIdParam,
    };
    if (sublocParam) params.sublocation = sublocParam;
    if (skillsParam) params.skills = skillsParam;
    if (industryParam) params.industry = industryParam;
    if (minExpParam && !isNaN(parseInt(minExpParam))) params.minExperience = parseInt(minExpParam);
    if (maxExpParam && !isNaN(parseInt(maxExpParam))) params.maxExperience = parseInt(maxExpParam);
    if (nameParam) params.name = nameParam;

    fetchCandidates(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Handle refresh button
  const handleRefresh = () => {
    setCandidates(new Map().set(1, []));
    // Trigger refetch by updating search params to current values
    updateSearchParams({ page: currentPage.toString() });
  };

  // Handle filter input changes - only updates URL, useEffect handles the rest
  const handleFilterChange = (filterName: string, value: string | undefined) => {
    // Convert undefined to empty string for URL
    const stringValue = value || "";

    // Update URL params - this triggers the useEffect
    const updates: Record<string, string> = { [filterName]: stringValue };

    // Reset to page 1 when any filter changes (except page itself)
    if (filterName !== "page") {
      updates.page = "1";
    }

    updateSearchParams(updates);
  };

  // Toggle column visibility
  const toggleColumn = (columnKey: ColumnKey) => {
    const newColumns = { ...visibleColumns, [columnKey]: !visibleColumns[columnKey] };
    setVisibleColumns(newColumns);
    updateSearchParams({ columns: encodeURIComponent(JSON.stringify(newColumns)) });
  };

  // Handle resume migration
  const handleMigrateResume = async (candidate: ExternalCandidate) => {
    if (!candidate.resumeUrl) return;
    
    setMigrationLoading(prev => new Map(prev).set(candidate.candidateId, true));
    
    try {
      await externalCandidatesService.migrateResume({
        externalUrl: candidate.resumeUrl,
        candidateName: candidate.name || `Candidate_${candidate.candidateId}`,
      });
      
      // Refresh data after migration
      handleRefresh();
    } catch (err: any) {
      console.error("Migration error:", err);
      setError(err.message || "Failed to migrate resume");
    } finally {
      setMigrationLoading(prev => {
        const newMap = new Map(prev);
        newMap.delete(candidate.candidateId);
        return newMap;
      });
    }
  };

  // Handle view resume
  const handleViewResume = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Render resume actions cell
  const renderResumeActions = (candidate: ExternalCandidate) => {
    const isLoading = migrationLoading.get(candidate.candidateId) || false;
    
    // If internal URL exists, show "View Resume" button
    if (candidate.resumeUrlInternal) {
      return (
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleViewResume(candidate.resumeUrlInternal!)}
        >
          View Resume
        </Button>
      );
    }
    
    // If only external URL exists, show Preview and Migrate buttons
    if (candidate.resumeUrl) {
      return (
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleViewResume(candidate.resumeUrl!)}
          >
            Preview
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={isLoading}
            disabled={isLoading}
            onClick={() => handleMigrateResume(candidate)}
          >
            Migrate
          </Button>
        </div>
      );
    }
    
    return <span className="text-gray-400">No Resume</span>;
  };

  // Render table cell based on column
  const renderTableCell = (column: ColumnKey, candidate: ExternalCandidate) => {
    switch (column) {
      case "Name":
        return <span className="font-medium text-gray-900">{candidate.name || '-'}</span>;
      case "Age":
        return candidate.age || '-';
      case "Gender":
        return candidate.gender || '-';
      case "YearsExperience":
        return candidate.yearsExperience || '-';
      case "CurrentSalary":
        return candidate.currentSalary || '-';
      case "Skills":
        return (
          <div className="max-w-xs truncate" title={candidate.skills || ''}>
            {candidate.skills || '-'}
          </div>
        );
      case "Industry":
        return candidate.industry || '-';
      case "Location":
        return candidate.location || '-';
      case "Sublocation":
        return candidate.sublocation || '-';
      case "QualificationLevel":
        return candidate.qualificationLevel || '-';
      case "Qualification":
        return (
          <div className="max-w-xs truncate" title={candidate.qualification || ''}>
            {candidate.qualification || '-'}
          </div>
        );
      case "PreviousJob":
        return candidate.previousJob || '-';
      case "Languages":
        return candidate.languages || '-';
      case "ResumeActions":
        return renderResumeActions(candidate);
      default:
        return null;
    }
  };

  // Pagination handlers - update URL, useEffect handles the rest
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      updateSearchParams({ page: (currentPage + 1).toString() });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      updateSearchParams({ page: (currentPage - 1).toString() });
    }
  };

  const getPageNumbers = (): number[] => {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    updateSearchParams({
      itemsPerPage: newItemsPerPage.toString(),
      page: "1"
    });
  };

  // Get visible columns array
  const visibleColumnsArray = COLUMN_OPTIONS.filter(col => visibleColumns?.[col.key]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{pageTitle}</h1>
        <p className="text-gray-600 mt-2">{pageDescription}</p>
      </div>

      {/* Action Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Button onClick={handleRefresh} loading={loading} disabled={loading}>
          Refresh
        </Button>
        
        {/* Column Toggle Button */}
        <div className="relative" ref={columnDropdownRef}>
          <Button
            variant="secondary"
            onClick={() => setShowColumnDropdown(!showColumnDropdown)}
            className="flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
            Columns
            <span className="bg-primary-lightGreen text-white text-xs rounded-full px-2 py-0.5">
              {visibleColumnsArray?.length ?? 0}
            </span>
          </Button>
          
          {/* Column Dropdown */}
          {showColumnDropdown && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
              <div className="p-3 border-b border-gray-100">
                <h4 className="font-medium text-gray-900">Show/Hide Columns</h4>
              </div>
              <div className="max-h-80 overflow-y-auto p-2">
                {COLUMN_OPTIONS.map(({ key, label }) => (
                  <label
                    key={key}
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns[key]}
                      onChange={() => toggleColumn(key)}
                      className="w-4 h-4 text-primary-lightGreen border-gray-300 rounded focus:ring-primary-lightGreen"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
              <div className="p-2 border-t border-gray-100">
                <button
                  onClick={() => {
                    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
                    updateSearchParams({ columns: encodeURIComponent(JSON.stringify(DEFAULT_VISIBLE_COLUMNS)) });
                  }}
                  className="w-full text-sm text-gray-600 hover:text-gray-900 py-1 px-2 rounded hover:bg-gray-100"
                >
                  Reset to Default
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Candidates</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select
            label="City"
            placeholder="Select City"
            options={cityOptions}
            value={(cityId || 147).toString()}
            onChange={(value) => handleFilterChange("cityId", value || undefined)}
            className="w-full"
            native={false}
          />
          
          <Input
            label="Sublocation"
            placeholder="Enter sublocation"
            value={sublocation}
            onChange={(e) => handleFilterChange("sublocation", e.target.value)}
          />
          
          <Input
            label="Skills"
            placeholder="Search skills (ILIKE)"
            value={skills}
            onChange={(e) => handleFilterChange("skills", e.target.value)}
          />
          
          <Input
            label="Industry"
            placeholder="Enter industry"
            value={industry}
            onChange={(e) => handleFilterChange("industry", e.target.value)}
          />
          
          <Input
            label="Min Experience"
            type="number"
            placeholder="Years"
            value={minExperience}
            onChange={(e) => handleFilterChange("minExperience", e.target.value)}
          />
          
          <Input
            label="Max Experience"
            type="number"
            placeholder="Years"
            value={maxExperience}
            onChange={(e) => handleFilterChange("maxExperience", e.target.value)}
          />
          
          <Input
            label="Name"
            placeholder="Search by name"
            value={nameFilter}
            onChange={(e) => handleFilterChange("name", e.target.value)}
          />
        </div>
        
        {/* Clear Filters Button */}
        {(cityId !== 147 || sublocation || skills || industry || minExperience || maxExperience || nameFilter) && (
          <div className="mt-4 flex justify-end">
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                updateSearchParams({
                  cityId: null,
                  sublocation: null,
                  skills: null,
                  industry: null,
                  minExperience: null,
                  maxExperience: null,
                  name: null,
                  page: "1"
                });
              }}
            >
              Clear All Filters
            </Button>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <Loader size="lg" />
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="text-center py-12 bg-red-50 rounded-lg border border-red-200">
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 48 48"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-red-800">Error Loading Candidates</h3>
          <p className="mt-1 text-red-600">{error}</p>
          <Button onClick={handleRefresh} className="mt-4">
            Try Again
          </Button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredCandidates?.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 48 48"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">No Candidates Found</h3>
          <p className="mt-1 text-gray-500">
            Try adjusting your filters or check back later.
          </p>
          {(cityId !== 147 || sublocation || skills || industry || minExperience || maxExperience || nameFilter) && (
            <Button
              onClick={() => {
                updateSearchParams({
                  cityId: null,
                  sublocation: null,
                  skills: null,
                  industry: null,
                  minExperience: null,
                  maxExperience: null,
                  name: null,
                  page: "1"
                });
              }}
              variant="secondary"
              className="mt-4"
            >
              Clear Filters
            </Button>
          )}
        </div>
      )}

      {/* Data Table */}
      {!loading && !error && filteredCandidates?.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {visibleColumnsArray.map(({ key, label }) => (
                    <th
                      key={key}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCandidates?.map((candidate: ExternalCandidate) => (
                  <tr key={candidate.candidateId} className="hover:bg-gray-50">
                    {visibleColumnsArray.map(({ key }) => (
                      <td key={key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {renderTableCell(key, candidate)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            onPageChange={(page) => handleFilterChange("page", page.toString())}
            onNextPage={goToNextPage}
            onPreviousPage={goToPreviousPage}
            getPageNumbers={getPageNumbers}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default ExternalCandidatesTable;
